import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
    AudioFile,
    VideoLibrary,
    PhotoLibrary,
    Image as ImageIcon,
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useEffect, useRef } from 'react';
import SlideUpTransition from '../components/SlideUpTransition';
import { Thread, ThreadMediaType } from '../database/forum';
import { CreateThreadRequest } from '../api/forumApi';
import { useTheme } from '@mui/material';
import { getConfig } from '../config';
import MentionText from '../components/MentionText';
import ThreadMedia from './ThreadMedia';
import AudioPlayer from '../bts/components/audio/AudioPlayer';
import VideoPlayer from '../bts/components/video/VideoPlayer';
import MentionInput, { MentionInputHandle } from '../components/MentionInput';

interface CreateThreadDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (req: CreateThreadRequest, file?: File) => Promise<void>;
    replyToThread?: Thread;
    forumSk: string;
    topicSk: string;
}

const CreateThreadDialog: React.FC<CreateThreadDialogProps> = ({
    open,
    onClose,
    onSubmit,
    replyToThread,
    forumSk,
    topicSk,
}) => {
    const [contentText, setContentText] = useState('');
    const mentionInputRef = useRef<MentionInputHandle>(null);
    const [mediaType, setMediaType] = useState<ThreadMediaType>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>();
    const [fileToUpload, setFileToUpload] = useState<File>();
    const [fileError, setFileError] = useState<string>('');
    const theme = useTheme();
    const forumMediaBucket = getConfig().media.forumMediaBucket;

    // Reset form when dialog opens/closes or replyToThread changes
    useEffect(() => {
        if (!open) {
            setContentText('');
            setMediaType(undefined);
            setPreviewUrl('');
            setFileToUpload(undefined);
            setFileError('');
        }
    }, [open, replyToThread]);

    const validateFile = (
        file: File,
        selectedMediaType: string,
    ): { isValid: boolean; error: string } => {
        if (selectedMediaType === ThreadMediaType.AUDIO) {
            const allowedAudioTypes = [
                'audio/mpeg',
                'audio/mp3',
                'audio/wav',
                'audio/wave',
                'audio/x-wav',
                'audio/aac',
                'audio/mp4',
                'audio/x-m4a',
                'audio/ogg',
                'audio/flac',
                'audio/x-flac',
            ];
            const audioFileExtensions = ['.mp3', '.wav', '.aac', '.m4a', '.ogg', '.flac'];
            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            const isValidMimeType = allowedAudioTypes.includes(file.type.toLowerCase());
            const isValidExtension = audioFileExtensions.includes(fileExtension);

            if (
                !file.type.startsWith('audio/') ||
                (!isValidMimeType && !isValidExtension)
            ) {
                return {
                    isValid: false,
                    error: 'Only audio files are allowed. Supported formats: MP3, WAV, AAC, M4A, OGG, FLAC',
                };
            }
        } else if (selectedMediaType === ThreadMediaType.VIDEO) {
            const allowedVideoTypes = [
                'video/mp4',
                'video/webm',
                'video/ogg',
                'video/mov',
                'video/quicktime',
                'video/avi',
                'video/x-msvideo',
                'video/mkv',
                'video/x-matroska',
            ];
            const videoFileExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            const isValidMimeType = allowedVideoTypes.includes(file.type.toLowerCase());
            const isValidExtension = videoFileExtensions.includes(fileExtension);

            if (
                !file.type.startsWith('video/') ||
                (!isValidMimeType && !isValidExtension)
            ) {
                return {
                    isValid: false,
                    error: 'Only video files are allowed. Supported formats: MP4, WebM, OGG, MOV, AVI, MKV',
                };
            }
        } else if (selectedMediaType === ThreadMediaType.IMAGE) {
            const allowedImageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/webp',
            ];
            const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            const isValidMimeType = allowedImageTypes.includes(file.type.toLowerCase());
            const isValidExtension = imageFileExtensions.includes(fileExtension);

            if (
                !file.type.startsWith('image/') ||
                (!isValidMimeType && !isValidExtension)
            ) {
                return {
                    isValid: false,
                    error: 'Only image files are allowed. Supported formats: JPEG, PNG, GIF, WebP',
                };
            }
        }

        return { isValid: true, error: '' };
    };

    const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        if (!file || !mediaType) return;

        setFileError('');

        const validation = validateFile(file, mediaType);
        if (!validation.isValid) {
            setFileError(validation.error);
            event.target.value = '';
            return;
        }

        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
        }

        setFileToUpload(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = async () => {
        if (!contentText.trim() && !mediaType) return;

        // If media type is selected, file must be uploaded
        if (mediaType && !fileToUpload) {
            setFileError('Please upload a file or deselect the media type');
            return;
        }

        setIsSubmitting(true);
        try {
            const serialized =
                mentionInputRef.current?.getSerializedValue() ?? contentText;
            const req: CreateThreadRequest = {
                contentText: serialized.trim() || undefined,
                mediaType,
                replyToThreadId: replyToThread?.sk,
            };
            if (replyToThread) {
                req.quotedThread = {
                    sk: replyToThread.sk,
                    username: replyToThread.username,
                    displayName: replyToThread.displayName,
                    isArtist: replyToThread.isArtist,
                    subscriptionStatus: replyToThread.subscriptionStatus,
                    subscriptionOverride: replyToThread.subscriptionOverride,
                    quote: replyToThread.quote,
                    contentText: replyToThread.contentText,
                    mediaType: replyToThread.mediaType,
                    createdAt: replyToThread.createdAt,
                    updatedAt: replyToThread.updatedAt,
                };
            }
            await onSubmit(req, fileToUpload);
            setContentText('');
            setMediaType(undefined);
            setPreviewUrl('');
            setFileToUpload(undefined);
            setFileError('');
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setContentText('');
            setMediaType(undefined);
            setPreviewUrl('');
            setFileToUpload(undefined);
            setFileError('');
            onClose();
        }
    };

    const handleRemoveFile = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl('');
        setFileToUpload(undefined);
        setFileError('');
    };

    const getUploadIcon = () => {
        switch (mediaType) {
            case ThreadMediaType.AUDIO:
                return <AudioFile />;
            case ThreadMediaType.VIDEO:
                return <VideoLibrary />;
            case ThreadMediaType.IMAGE:
                return <PhotoLibrary />;
        }
    };

    const getFileAccept = () => {
        switch (mediaType) {
            case ThreadMediaType.AUDIO:
                return '.mp3,.wav,.aac,.m4a,.ogg,.flac,audio/mpeg,audio/wav,audio/aac,audio/mp4,audio/ogg,audio/flac';
            case ThreadMediaType.VIDEO:
                return '.mp4,.webm,.ogg,.mov,.avi,.mkv,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska';
            case ThreadMediaType.IMAGE:
                return '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp';
        }
    };

    const getMediaUrl = (thread: Thread) => {
        if (!thread.mediaType) return undefined;
        // Encode each path segment to handle # and other special characters
        const encodedForumSk = encodeURIComponent(forumSk);
        const encodedPk = encodeURIComponent(thread.pk);
        const encodedSk = encodeURIComponent(thread.sk);
        const encodedUsername = encodeURIComponent(thread.username);

        return `${forumMediaBucket}/${encodedForumSk}/${encodedPk}/${encodedSk}/${encodedUsername}`;
    };

    const renderQuotedThread = () => {
        if (!replyToThread) return null;

        const mediaUrl = getMediaUrl(replyToThread);

        return (
            <Box
                sx={{
                    p: 2,
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: 1,
                }}
            >
                <Typography variant='caption' gutterBottom>
                    Replying to {replyToThread.displayName}:
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                    {replyToThread.contentText && (
                        <MentionText variant='body2' sx={{ opacity: 0.8 }}>
                            {replyToThread.contentText}
                        </MentionText>
                    )}
                    {replyToThread.mediaType && mediaUrl && (
                        <ThreadMedia
                            mediaUrl={mediaUrl}
                            mediaType={replyToThread.mediaType}
                        />
                    )}
                </Stack>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            TransitionComponent={SlideUpTransition}
        >
            <DialogTitle variant='h5'>
                {replyToThread ? 'Reply to Thread' : 'Create New Thread'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    {renderQuotedThread()}

                    <Typography variant='body2'>
                        Add text, media, or both to continue the conversation
                    </Typography>

                    <MentionInput
                        ref={mentionInputRef}
                        label='Message'
                        value={contentText}
                        onChange={setContentText}
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={8}
                        placeholder='Share your thoughts...'
                    />

                    <FormControl fullWidth>
                        <InputLabel>Media Type</InputLabel>
                        <Select
                            value={mediaType || ''}
                            label='Media Type'
                            onChange={(e) => {
                                const value = e.target.value as ThreadMediaType;
                                setMediaType(value || undefined);
                                // Clear file when media type changes
                                if (previewUrl) {
                                    URL.revokeObjectURL(previewUrl);
                                }
                                setPreviewUrl('');
                                setFileToUpload(undefined);
                                setFileError('');
                            }}
                        >
                            <MenuItem value=''>
                                <em>None</em>
                            </MenuItem>
                            <MenuItem value={ThreadMediaType.IMAGE}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <ImageIcon />
                                    <span>Image</span>
                                </Stack>
                            </MenuItem>
                            <MenuItem value={ThreadMediaType.VIDEO}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <VideoLibrary />
                                    <span>Video</span>
                                </Stack>
                            </MenuItem>
                            <MenuItem value={ThreadMediaType.AUDIO}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <AudioFile />
                                    <span>Audio</span>
                                </Stack>
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {mediaType && (
                        <Button
                            component='label'
                            variant='outlined'
                            startIcon={getUploadIcon()}
                            fullWidth
                        >
                            Upload {mediaType}
                            <input
                                type='file'
                                accept={getFileAccept()}
                                hidden
                                onChange={onChangeFile}
                            />
                        </Button>
                    )}

                    {fileError && (
                        <Alert severity='error' onClose={() => setFileError('')}>
                            {fileError}
                        </Alert>
                    )}

                    {previewUrl && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                maxHeight: '400px',
                                overflow: 'hidden',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                paddingTop: { xs: '44px', sm: 0 }, // Add padding for delete button on mobile
                            }}
                        >
                            {mediaType === ThreadMediaType.AUDIO ? (
                                <AudioPlayer src={previewUrl} />
                            ) : mediaType === ThreadMediaType.VIDEO ? (
                                <VideoPlayer src={previewUrl} />
                            ) : (
                                <img
                                    src={previewUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    alt='Preview'
                                />
                            )}
                            <IconButton
                                onClick={handleRemoveFile}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    zIndex: 10, // Ensure button stays on top
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant='contained'
                    loading={isSubmitting}
                    disabled={
                        (!contentText.trim() && !mediaType) ||
                        (mediaType && !fileToUpload)
                    }
                >
                    {replyToThread ? 'Reply' : 'Create Thread'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CreateThreadDialog;
